// Fragen und Montagetermin am Gerät (E3, S6 und S7).
//
// Fragen mit Zusammenhang (CLAUDE.md, Grundsatz 9): Das Büro fragt am Ding –
// an der Besichtigung, einer Station, einem Foto, einer Notiz –, der
// Verkäufer antwortet draußen, auch ohne Netz, setzt „erledigt“ und fragt
// selbst („Hausnummer ist 12a“ – einen Fehler nach dem Senden meldet er so,
// Gernot, 20.10.2026). Kein Chat: Frage und Antwort bleiben an ihrem Stück.
// Der Montagetermin (Gernots Entscheidung 4): Tag und Text, mit dem Kunden
// vereinbart – „ganz einfach“, und die Eingabe überlebt den Wechsel in den
// Kalender (`terminEntwurfSichern`).
//
// Wo was liegt (Felder an der Besichtigung, speicher.ts):
//  - Vom Server: `fragen` und `montagetermin` – die Kopie, wie GET
//    /api/doku/aufnahme/:id sie zuletzt brachte. Jedes Auffrischen ersetzt
//    sie ganz (einsicht.ts, `kopieAuffrischen`).
//  - Vom Gerät: `eigeneFragen`, `eigeneAntworten`, `eigeneStaende`,
//    `eigenerTermin` – was der Verkäufer gesagt hat. Daraus baut der
//    Postausgang beim Senden den Eintrag (postausgang.ts) – wie bei allem
//    anderen merkt er sich nur, DASS etwas hinaus muss.
//  - Gezeigt wird beides übereinander (`fragenAmGeraet`, `terminAmGeraet`):
//    Was vom Gerät kommt, steht sofort da – „wartet“, bis der Server es
//    bestätigt hat.
//
// Wann „vom Gerät“ wegkommt (`eigenesAufraeumen`, beim Auffrischen): erst,
// wenn die Kopie vom Server es sicher enthält. Fragen und Antworten stehen
// nach dem Anlegen fest – sie gehen, sobald ihre Kennung in der Kopie steht.
// „Erledigt“ und der Termin lassen sich ändern – sie gehen, wenn der Server
// sie bestätigt hatte, BEVOR die Kopie erfragt wurde, und nichts Neueres
// dazu im Postausgang wartet. Warum so streng: Eine Kopie, die der Server
// kurz vor dem Senden zusammenstellte, kennt die Änderung noch nicht – dann
// stünde für eine Weile wieder der alte Stand da, und der Postausgang fände
// womöglich nichts mehr, was er senden könnte.
//
// Gefragt und geantwortet wird nur an einer gesendeten Besichtigung – vorher
// gehört sie noch ganz dem Verkäufer, und das Büro sieht sie nicht. Der
// Server nimmt alle vier Arten jederzeit an, nie als Teil eines Nachtrags
// (server/src/doku/eintraege.ts, JEDERZEIT_ERLAUBT).

import {
  BUERO_GRENZEN,
  montageterminEingabe,
  neueKennung,
  STATIONEN,
  type Eintrag,
  type FrageAmServer,
  type FrageBezug,
  type FrageStand,
  type NutzerKurz,
} from "@happy-energy/kern";
import {
  speicher,
  type Aufnahme,
  type EigeneAntwort,
  type EigeneFrage,
  type EigenerStand,
  type Foto,
  type Notiz,
  type PostArt,
  type Station,
  type TerminEntwurf,
  type VomGeraet,
} from "./speicher.ts";
import { postausgangTabellen, vormerken } from "./postausgang.ts";

// --- Was die App zeigt: Server und Gerät übereinander -------------------------------------

/** Eine Antwort, wie die App sie zeigt – vom Server oder vom Gerät („wartet“, bis der Server sie hat). */
export interface AntwortAmGeraet {
  id: string;
  text: string;
  von: NutzerKurz;
  geantwortetAm: string;
  wartet: boolean;
}

/** Eine Frage, wie die App sie zeigt: die vom Server, darüber, was das Gerät dazu gesagt hat. */
export interface FrageAmGeraet extends Omit<FrageAmServer, "antworten"> {
  antworten: AntwortAmGeraet[];
  /** Die Frage selbst kommt vom Gerät und ist noch nicht beim Server. */
  wartet: boolean;
  /** „Erledigt“ oder „wieder offen“ kommt vom Gerät und ist noch nicht beim Server. */
  standWartet: boolean;
}

type MitGespraech = Pick<Aufnahme, "id" | "fragen" | "eigeneFragen" | "eigeneAntworten" | "eigeneStaende">;

/**
 * Die Fragen einer Besichtigung, wie die App sie zeigt – älteste zuerst, je
 * mit ihren Antworten (älteste zuerst): die Kopie vom Server, dazu, was vom
 * Gerät noch nicht in ihr steht. Ein „erledigt“ vom Gerät gilt vor dem
 * Stand des Servers, solange es nicht aufgeräumt ist.
 */
export function fragenAmGeraet(a: MitGespraech): FrageAmGeraet[] {
  const liste: FrageAmGeraet[] = (a.fragen ?? []).map((f) => ({
    ...f,
    antworten: f.antworten.map((x) => ({ ...x, wartet: false })),
    wartet: false,
    standWartet: false,
  }));
  const jeKennung = new Map(liste.map((f) => [f.id, f]));
  for (const f of a.eigeneFragen ?? []) {
    if (jeKennung.has(f.id)) continue;
    const neu: FrageAmGeraet = {
      id: f.id,
      aufnahmeId: a.id,
      bezug: { ...f.bezug },
      text: f.text,
      von: f.von,
      gestelltAm: f.gestelltAm,
      stand: "offen",
      erledigtAm: null,
      erledigtVon: null,
      antworten: [],
      wartet: f.bestaetigtAm === null,
      standWartet: false,
    };
    liste.push(neu);
    jeKennung.set(neu.id, neu);
  }
  for (const x of a.eigeneAntworten ?? []) {
    const f = jeKennung.get(x.frageId);
    if (!f || f.antworten.some((y) => y.id === x.id)) continue;
    f.antworten.push({ id: x.id, text: x.text, von: x.von, geantwortetAm: x.geantwortetAm, wartet: x.bestaetigtAm === null });
  }
  for (const s of a.eigeneStaende ?? []) {
    const f = jeKennung.get(s.frageId);
    if (!f) continue;
    if (f.stand !== s.stand) {
      f.stand = s.stand;
      f.erledigtAm = s.stand === "erledigt" ? s.am : null;
      f.erledigtVon = s.stand === "erledigt" ? s.von : null;
    }
    f.standWartet = s.bestaetigtAm === null;
  }
  for (const f of liste) f.antworten.sort((x, y) => x.geantwortetAm.localeCompare(y.geantwortetAm) || x.id.localeCompare(y.id));
  return liste.sort((x, y) => x.gestelltAm.localeCompare(y.gestelltAm) || x.id.localeCompare(y.id));
}

/**
 * Wartet diese Frage auf mich? Offen – und das letzte Wort (die jüngste
 * Antwort, sonst die Frage selbst) ist nicht von mir. Hat der Verkäufer
 * geantwortet, ist das Büro dran, bis es „erledigt“ setzt oder nachfragt;
 * so ruft das Band nicht weiter „antworten“, wenn schon geantwortet ist.
 */
export function wartetAufMich(f: Pick<FrageAmGeraet, "stand" | "von" | "antworten">, ichId: string): boolean {
  if (f.stand !== "offen") return false;
  const letzte = f.antworten.at(-1);
  return (letzte ? letzte.von.id : f.von.id) !== ichId;
}

/** Die offenen Fragen dieser Besichtigung, die auf mich warten – älteste zuerst. */
export function offeneFuerMich(a: MitGespraech, ichId: string): FrageAmGeraet[] {
  return fragenAmGeraet(a).filter((f) => wartetAufMich(f, ichId));
}

/** Der Montagetermin, wie die App ihn zeigt: was das Gerät zuletzt gespeichert hat, sonst die Kopie vom Server. */
export interface TerminAmGeraet {
  am: string | null;
  text: string | null;
  /** Vom Gerät, noch nicht beim Server. */
  wartet: boolean;
}

export function terminAmGeraet(a: Pick<Aufnahme, "eigenerTermin" | "montagetermin">): TerminAmGeraet {
  if (a.eigenerTermin) return { am: a.eigenerTermin.am, text: a.eigenerTermin.text, wartet: a.eigenerTermin.bestaetigtAm === null };
  return { am: a.montagetermin?.am ?? null, text: a.montagetermin?.text ?? null, wartet: false };
}

// --- Wo eine Frage steht ---------------------------------------------------------------

/** Was eine Frage über ihren Platz wissen muss – aus den Stücken der Besichtigung am Gerät. */
export interface BesichtigungsTeile {
  stationen: readonly Pick<Station, "id" | "nr">[];
  fotos: readonly Pick<Foto, "id" | "stationId" | "reihenfolge">[];
  notizen: readonly Pick<Notiz, "id" | "stationId" | "fotoId">[];
}

/** Wo eine Frage steht: auf welcher Seite, wie man es sagt, an welcher Station, an welchem Foto. */
export interface FrageOrt {
  adresse: string;
  /** Für Menschen: „an der Besichtigung“, „an Station 3 · Übergabepunkt“, „an Foto 2 · Station 7“. */
  wo: string;
  /** Die Station, zu der das Stück gehört – null an Besichtigung und Anlage (und wenn das Gerät das Stück nicht kennt). */
  stationId: string | null;
  /** Das Foto, wenn die Frage an einem Foto oder seiner Notiz hängt. */
  fotoId: string | null;
}

function stationName(nr: number): string {
  return STATIONEN.find((s) => s.nr === nr)?.name ?? `Station ${nr}`;
}

/**
 * Wo eine Frage hingehört. Kennt das Gerät das Stück nicht (etwa ein Foto
 * aus einem Nachtrag, der woanders begonnen wurde), steht sie oben an der
 * Besichtigung – da, wo man zuerst hinsieht, damit keine still fehlt.
 */
export function frageOrt(teile: BesichtigungsTeile, aufnahmeId: string, bezug: { tabelle: FrageBezug; id: string }): FrageOrt {
  const besichtigung: FrageOrt = { adresse: `#/aufnahme/${aufnahmeId}`, wo: "an der Besichtigung", stationId: null, fotoId: null };
  const anStation = (stationId: string | null, fotoId: string | null, notiz: boolean): FrageOrt => {
    const s = teile.stationen.find((x) => x.id === stationId);
    if (!s) return besichtigung;
    const stationAdresse = `#/aufnahme/${aufnahmeId}/station/${s.nr}`;
    if (fotoId === null) return { adresse: stationAdresse, wo: `${notiz ? "an der Notiz · " : "an "}Station ${s.nr} · ${stationName(s.nr)}`, stationId: s.id, fotoId: null };
    const fotos = teile.fotos.filter((f) => f.stationId === s.id).sort((x, y) => x.reihenfolge - y.reihenfolge);
    const nr = fotos.findIndex((f) => f.id === fotoId) + 1;
    if (nr === 0) return besichtigung;
    return { adresse: `${stationAdresse}/foto/${fotoId}`, wo: `${notiz ? "an der Notiz zu " : "an "}Foto ${nr} · Station ${s.nr}`, stationId: s.id, fotoId };
  };
  switch (bezug.tabelle) {
    case "anlage":
    case "aufnahme":
      return besichtigung;
    case "station":
      return anStation(bezug.id, null, false);
    case "foto":
      return anStation(teile.fotos.find((f) => f.id === bezug.id)?.stationId ?? null, bezug.id, false);
    case "notiz": {
      const n = teile.notizen.find((x) => x.id === bezug.id);
      if (!n) return besichtigung;
      if (n.fotoId !== null) return anStation(teile.fotos.find((f) => f.id === n.fotoId)?.stationId ?? null, n.fotoId, true);
      return anStation(n.stationId, null, true);
    }
  }
}

/** Steht die Frage oben an der Besichtigung? An Anlage und Besichtigung – und alles, dessen Stück das Gerät nicht kennt. */
export function obenAnDerBesichtigung(teile: BesichtigungsTeile, aufnahmeId: string, f: Pick<FrageAmGeraet, "bezug">): boolean {
  return frageOrt(teile, aufnahmeId, f.bezug).stationId === null;
}

/** Was das Band auf der Startseite braucht. */
export interface FragenUeberblick {
  /** Alle offenen Fragen, die auf mich warten. */
  zahl: number;
  /** Je Besichtigung, wie viele – für den Chip unter „Gesendet“. */
  jeAufnahme: Record<string, number>;
  /** Die älteste davon – dorthin führt das Band. */
  erste: { aufnahmeId: string; frageId: string; kundeName: string; ort: FrageOrt } | null;
}

/**
 * Die offenen Fragen aller gesendeten Besichtigungen am Gerät, die auf mich
 * warten – aus den Kopien (die Zahl der Liste „Gesendet“ zählt alle offenen,
 * auch die, auf die ich schon geantwortet habe; daraus frischt der Abgleich
 * die Kopien auf, einsicht.ts). Für das Band oben auf der Startseite.
 */
export async function fragenUeberblickLesen(ichId: string): Promise<FragenUeberblick> {
  const aufnahmen = await speicher.aufnahmen.where("stand").anyOf(["abgeschlossen", "im_buero"]).toArray();
  let zahl = 0;
  const jeAufnahme: Record<string, number> = {};
  let erste: { a: Aufnahme; f: FrageAmGeraet } | null = null;
  for (const a of aufnahmen) {
    const offen = offeneFuerMich(a, ichId);
    if (offen.length === 0) continue;
    zahl += offen.length;
    jeAufnahme[a.id] = offen.length;
    const f = offen[0]!;
    if (!erste || f.gestelltAm < erste.f.gestelltAm) erste = { a, f };
  }
  if (!erste) return { zahl, jeAufnahme, erste: null };
  const id = erste.a.id;
  const [stationen, fotos, notizen] = await Promise.all([
    speicher.stationen.where("aufnahmeId").equals(id).toArray(),
    speicher.fotos.where("aufnahmeId").equals(id).toArray(),
    speicher.notizen.where("aufnahmeId").equals(id).toArray(),
  ]);
  return {
    zahl,
    jeAufnahme,
    erste: { aufnahmeId: id, frageId: erste.f.id, kundeName: erste.a.kunde.name, ort: frageOrt({ stationen, fotos, notizen }, id, erste.f.bezug) },
  };
}

// --- Was der Verkäufer tut -----------------------------------------------------------------

/** Gefragt und geantwortet wird an einer gesendeten Besichtigung – vorher gehört sie noch ganz dem Verkäufer. */
export function gespraechMoeglich(a: Pick<Aufnahme, "stand">): boolean {
  return a.stand !== "in_aufnahme";
}

/** Der Text einer Frage oder Antwort, wie er hinausgeht: getrimmt – null, wenn leer oder zu lang (Vertrag). */
export function gespraechText(text: string): string | null {
  const t = text.trim();
  return t !== "" && t.length <= BUERO_GRENZEN.frageText ? t : null;
}

/**
 * In einem Zug mit dem Postausgang an der Besichtigung arbeiten – nur, wenn
 * es sie gibt und sie gesendet ist. Sonst null, und es ändert sich nichts.
 */
function anDerBesichtigung<T>(aufnahmeId: string, arbeit: (a: Aufnahme) => Promise<T>): Promise<T | null> {
  return speicher.transaction("rw", [...postausgangTabellen()], async () => {
    const a = await speicher.aufnahmen.get(aufnahmeId);
    if (!a || !gespraechMoeglich(a)) return null;
    return arbeit(a);
  });
}

/**
 * „Frage stellen“ – an der Besichtigung, einer Station oder einem Foto. Die
 * Kennung entsteht hier; die Frage steht sofort da („wartet“) und geht über
 * den Postausgang hinaus. Eine Frage an der Besichtigung trägt deren
 * Kennung (Vertrag). Gibt die Kennung zurück – null, wenn der Text leer ist
 * oder die Besichtigung (noch) nicht gesendet.
 */
export async function frageStellen(
  aufnahmeId: string,
  bezug: { tabelle: FrageBezug; id: string },
  text: string,
  von: NutzerKurz,
  jetzt = new Date(),
): Promise<string | null> {
  const t = gespraechText(text);
  if (t === null) return null;
  return anDerBesichtigung(aufnahmeId, async (a) => {
    const f: EigeneFrage = {
      id: neueKennung(),
      bezug: bezug.tabelle === "aufnahme" ? { tabelle: "aufnahme", id: a.id } : bezug.tabelle === "anlage" ? { tabelle: "anlage", id: a.anlage.id } : { ...bezug },
      text: t,
      gestelltAm: jetzt.toISOString(),
      von: { id: von.id, name: von.name },
      bestaetigtAm: null,
    };
    await speicher.aufnahmen.update(a.id, { eigeneFragen: [...(a.eigeneFragen ?? []), f] });
    await vormerken("frage", f.id, a.id);
    return f.id;
  });
}

/** „Antworten“ – auf eine Frage dieser Besichtigung. Gibt die Kennung der Antwort zurück, null wie bei `frageStellen` (oder die Frage fehlt). */
export async function antworten(aufnahmeId: string, frageId: string, text: string, von: NutzerKurz, jetzt = new Date()): Promise<string | null> {
  const t = gespraechText(text);
  if (t === null) return null;
  return anDerBesichtigung(aufnahmeId, async (a) => {
    if (!fragenAmGeraet(a).some((f) => f.id === frageId)) return null;
    const x: EigeneAntwort = { id: neueKennung(), frageId, text: t, geantwortetAm: jetzt.toISOString(), von: { id: von.id, name: von.name }, bestaetigtAm: null };
    await speicher.aufnahmen.update(a.id, { eigeneAntworten: [...(a.eigeneAntworten ?? []), x] });
    await vormerken("antwort", x.id, a.id);
    return x.id;
  });
}

/**
 * „Erledigt“ – oder „wieder offen“. Je Frage gilt der jüngste Wunsch des
 * Geräts; tippt man zweimal hin und her, geht nur der letzte Stand hinaus
 * (der Postausgang hat je Frage einen Eintrag). Gibt zurück, ob sich etwas
 * geändert hat.
 */
export async function frageStandAendern(aufnahmeId: string, frageId: string, stand: FrageStand, von: NutzerKurz, jetzt = new Date()): Promise<boolean> {
  const geaendert = await anDerBesichtigung(aufnahmeId, async (a) => {
    const f = fragenAmGeraet(a).find((x) => x.id === frageId);
    if (!f || f.stand === stand) return false;
    const neu: EigenerStand = { frageId, stand, am: jetzt.toISOString(), von: { id: von.id, name: von.name }, bestaetigtAm: null };
    await speicher.aufnahmen.update(a.id, { eigeneStaende: [...(a.eigeneStaende ?? []).filter((s) => s.frageId !== frageId), neu] });
    await vormerken("frage-stand", frageId, a.id);
    return true;
  });
  return geaendert === true;
}

/**
 * Den Montagetermin, wie er gerade getippt wird, sofort am Gerät sichern –
 * bei jeder Änderung, nicht erst beim Speichern: Manuel schaut dazwischen in
 * seinen Kalender, und am iPhone kann die App im Hintergrund beendet werden
 * (Gernots Entscheidung 4). Null vergisst den Entwurf.
 */
export async function terminEntwurfSichern(aufnahmeId: string, entwurf: TerminEntwurf | null): Promise<void> {
  await speicher.aufnahmen.update(aufnahmeId, { terminEntwurf: entwurf });
}

/**
 * „Speichern“ am Montagetermin:
 *  - gespeichert: am Gerät und für den Server vorgemerkt (Kennung: die Anlage);
 *  - unveraendert: Er war schon so – der Entwurf ist weg, sonst nichts;
 *  - ungueltig: kein Tag, den es gibt, oder der Text zu lang;
 *  - gesperrt: Die Besichtigung ist nicht gesendet (oder fehlt).
 * Leerer Tag und leerer Text heißt: kein Termin.
 */
export type TerminErgebnis = "gespeichert" | "unveraendert" | "ungueltig" | "gesperrt";

export async function montageterminSpeichern(aufnahmeId: string, eingabe: TerminEntwurf, jetzt = new Date()): Promise<TerminErgebnis> {
  const geprueft = montageterminEingabe.safeParse({ am: eingabe.am.trim() === "" ? null : eingabe.am.trim(), text: eingabe.text });
  if (!geprueft.success) return "ungueltig";
  const { am, text } = geprueft.data;
  const ergebnis = await anDerBesichtigung(aufnahmeId, async (a): Promise<TerminErgebnis> => {
    const bisher = terminAmGeraet(a);
    if (bisher.am === am && bisher.text === text) {
      if (a.terminEntwurf) await speicher.aufnahmen.update(a.id, { terminEntwurf: null });
      return "unveraendert";
    }
    await speicher.aufnahmen.update(a.id, { eigenerTermin: { am, text, geaendertAm: jetzt.toISOString(), bestaetigtAm: null }, terminEntwurf: null });
    await vormerken("montagetermin", a.anlage.id, a.id);
    return "gespeichert";
  });
  return ergebnis ?? "gesperrt";
}

// --- Der Server hat es: vermerken und aufräumen ------------------------------------------------

const GESPRAECH_ARTEN: ReadonlySet<PostArt> = new Set(["frage", "antwort", "frage-stand", "montagetermin"]);

/** Gehört diese Art zum Gespräch an der Besichtigung (oder zum Montagetermin)? */
export function gespraechArt(art: PostArt): boolean {
  return GESPRAECH_ARTEN.has(art);
}

/** Setzt `bestaetigtAm` an dem, was passt und noch wartet – null, wenn sich nichts ändert. */
function bestaetigen<T extends VomGeraet>(liste: readonly T[] | undefined, passt: (x: T) => boolean, am: string): T[] | null {
  if (!liste?.some((x) => passt(x) && x.bestaetigtAm === null)) return null;
  return liste.map((x) => (passt(x) && x.bestaetigtAm === null ? { ...x, bestaetigtAm: am } : x));
}

/**
 * Der Server hat eine Frage, eine Antwort, „erledigt“ oder den Termin mit
 * „ok“ angenommen: am Gesagten vermerken, wann. Der Sender ruft das in der
 * Transaktion, in der er den Eintrag austrägt (postausgang.ts, `erledigt`) –
 * also nur, wenn sich seit dem Bauen nichts daran geändert hat; dazu passt
 * hier der Inhalt (derselbe Stand, derselbe Termin). Andere Arten: nichts.
 */
export async function amServerVermerken(e: Eintrag, jetzt: Date): Promise<void> {
  if (!gespraechArt(e.art)) return;
  const a = await speicher.aufnahmen.get(e.aufnahmeId);
  if (!a) return;
  const am = jetzt.toISOString();
  switch (e.art) {
    case "frage": {
      const eigeneFragen = bestaetigen(a.eigeneFragen, (x) => x.id === e.kennung, am);
      if (eigeneFragen) await speicher.aufnahmen.update(a.id, { eigeneFragen });
      return;
    }
    case "antwort": {
      const eigeneAntworten = bestaetigen(a.eigeneAntworten, (x) => x.id === e.kennung, am);
      if (eigeneAntworten) await speicher.aufnahmen.update(a.id, { eigeneAntworten });
      return;
    }
    case "frage-stand": {
      const eigeneStaende = bestaetigen(a.eigeneStaende, (x) => x.frageId === e.kennung && x.stand === e.stand, am);
      if (eigeneStaende) await speicher.aufnahmen.update(a.id, { eigeneStaende });
      return;
    }
    case "montagetermin": {
      const t = a.eigenerTermin;
      if (t && t.bestaetigtAm === null && t.am === e.am && t.text === e.text) await speicher.aufnahmen.update(a.id, { eigenerTermin: { ...t, bestaetigtAm: am } });
      return;
    }
    default:
      return;
  }
}

/** Was vom Gerät nach einem Auffrischen bleibt – genau die vier Felder. */
export type EigenesNachher = Required<Pick<Aufnahme, "eigeneFragen" | "eigeneAntworten" | "eigeneStaende" | "eigenerTermin">>;

/**
 * Was vom Gerät bleibt, wenn die Kopie vom Server so aussieht (`fragen`,
 * erfragt um `gefragtAm`, Zeit des Geräts). Regeln oben im Kopf der Datei:
 * Fragen und Antworten gehen, sobald ihre Kennung in der Kopie steht; Stand
 * und Termin, wenn der Server sie vor `gefragtAm` bestätigt hatte und
 * nichts Neueres dazu wartet (`wartet`: steht das im Postausgang?).
 */
export function eigenesAufraeumen(
  a: Pick<Aufnahme, "anlage" | "eigeneFragen" | "eigeneAntworten" | "eigeneStaende" | "eigenerTermin">,
  fragen: readonly FrageAmServer[],
  gefragtAm: string,
  wartet: (art: PostArt, kennung: string) => boolean,
): EigenesNachher {
  const fragenDa = new Set(fragen.map((f) => f.id));
  const antwortenDa = new Set(fragen.flatMap((f) => f.antworten.map((x) => x.id)));
  const sicherDrin = (x: VomGeraet) => x.bestaetigtAm !== null && x.bestaetigtAm < gefragtAm;
  const t = a.eigenerTermin ?? null;
  return {
    eigeneFragen: (a.eigeneFragen ?? []).filter((f) => !fragenDa.has(f.id)),
    eigeneAntworten: (a.eigeneAntworten ?? []).filter((x) => !antwortenDa.has(x.id)),
    eigeneStaende: (a.eigeneStaende ?? []).filter((s) => !(sicherDrin(s) && !wartet("frage-stand", s.frageId))),
    eigenerTermin: t && sicherDrin(t) && !wartet("montagetermin", a.anlage.id) ? null : t,
  };
}
